import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Subscription } from 'rxjs';
import { generateId } from '../../../server/services/utils.service';
import type { OrgSpace } from '../../../server/types/org-types';
import { AuthUserService } from '../auth-user.service';
import {
  SearchComponent,
  type SearchDialogComponentData,
} from '../search/search.component';
import { type ListFilter, TaskService } from '../task.service';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

dayjs.extend(isoWeek);

@Component({
  selector: 'app-completed-history',
  templateUrl: './completed-history.component.html',
  styleUrls: ['./completed-history.component.scss'],
  standalone: false,
})
export class CompletedHistoryComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private utilsService = inject(UtilsService);
  private taskService = inject(TaskService);
  private changeDetector = inject(ChangeDetectorRef);
  private titleService = inject(TitleService);

  authUserId: string;
  orgId: string;
  spaceId: string;

  @ViewChild('listContainer')
  listContainerElement: ElementRef<HTMLTextAreaElement>;
  listContext: { listId: string; listFilter: ListFilter }[] = [];
  showFilter = true;
  selectDisplayTaskId: string | undefined = undefined;

  assignedTo?: string | null | undefined;
  tagIds: string[] | null;

  space: OrgSpace;

  pageState: 'waiting' | 'ready' | 'error' = 'waiting';

  lists: { listId: string; start: number; end: number; headerText: string }[] =
    [];

  subscriptions = new Subscription();

  @ViewChild('rightSpacer') rightSpacer: ElementRef;

  ngOnInit(): void {
    this.titleService.set('Completed history');

    this.orgId = this.route.snapshot.params.orgId;
    this.spaceId = this.route.snapshot.params.spaceId;

    const { userId, tagIds } = this.utilsService.taskFilterQueryParams(
      this.route.snapshot.queryParams
    );
    this.assignedTo = userId;
    this.tagIds = tagIds;

    const userOrgObj = this.authUserService.getAuthUserOrgObj({});
    const space = userOrgObj.org.spaces.find((s) => s._id === this.spaceId);

    if (!space) {
      this.pageState = 'error';
      return;
    }

    this.space = space;

    this.taskService.refreshTaskData({
      orgId: userOrgObj.org._id,
      spaceId: this.spaceId,
    });

    let listId = generateId();
    let referenceTime = Date.now();
    let start = this.utilsService.getStartOfWeek(referenceTime);
    let end = this.utilsService.getEndOfWeek(referenceTime);
    let headerText =
      'Week ' +
      dayjs(start).isoWeek() +
      ' - ' +
      new Date(start).toLocaleDateString();
    this.lists.push({ listId, start, end, headerText });
    const oldestDate = this.taskService.getOldestCompletedTask({
      spaceId: this.spaceId,
    });

    let count = 0;
    while (start > oldestDate && count < 1000) {
      // Go to previous week
      listId = generateId();
      referenceTime = start - 1;
      start = this.utilsService.getStartOfWeek(referenceTime);
      end = this.utilsService.getEndOfWeek(referenceTime);
      headerText =
        'Week ' +
        dayjs(start).isoWeek() +
        ' - ' +
        new Date(start).toLocaleDateString();
      this.lists.unshift({ listId, start, end, headerText });

      count++;
    }

    setTimeout(() => {
      if (this.rightSpacer)
        this.rightSpacer.nativeElement.scrollIntoView({
          block: 'end',
          inline: 'end',
        });
    }, 1);
  }

  // Start #PageListDuplicate

  toggleFilter() {
    this.showFilter = !this.showFilter;
    this.changeDetector.detectChanges();
  }

  openSearch() {
    const dialogConfig = new MatDialogConfig();
    const { orgId, spaceId, listContext } = this;
    const data: SearchDialogComponentData = {
      orgId,
      spaceId,
      listContext,
    };
    dialogConfig.data = data;
    dialogConfig.position = { top: '75px' }; // #filterAutocompleteHeight
    const dialogRef = this.dialog.open(SearchComponent, dialogConfig);

    const sub = dialogRef.afterClosed().subscribe((result) => {
      //
    });
    this.subscriptions.add(sub);
  }
  // End #PageListDuplicate

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
